import { useNavigate } from 'react-router-dom';
import {
  Download,
  Folder,
  FileText,
  FolderInput,
  History,
  Info,
  MessageSquare,
  MessagesSquare,
  MoreHorizontal,
  Pencil,
  Share2,
  ShieldAlert,
  SquarePen,
  Star,
  Trash2,
} from 'lucide-react';
import { formatBytes, formatDate } from '../../../utils/format';
import { isEditableInEditor } from '../../editor/editorApi';
import ImageThumb, { isImageFile } from './ImageThumb';
import FileAvailability from './FileAvailability';
import MalwareBadge from './MalwareBadge';
import { isMalwareBlocked } from '../malware';

/**
 * Secondary actions stay out of the way on phones (where ten icons cannot fit)
 * and move into the row's context menu, reachable from the overflow button.
 */
const SECONDARY = 'hidden md:inline-flex';

function IconButton({ title, onClick, children, danger, active, className = '' }) {
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className={`rounded-md p-1.5 transition-colors hover:bg-slate-100 ${
        active ? 'text-amber-500' : 'text-slate-400 group-hover:text-slate-500'
      } ${danger ? 'hover:text-red-600' : 'hover:text-brand-600'} ${className}`}
    >
      {children}
    </button>
  );
}

/** The count bubble on the comments/chat icons; `needsAttention` colours it. */
function ActivityBadge({ count, needsAttention }) {
  return (
    <span
      className={`absolute -right-1 -top-1 inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full px-0.5 text-[9px] font-semibold text-white ${
        needsAttention ? 'bg-brand-600' : 'bg-slate-400'
      }`}
    >
      {count}
    </span>
  );
}

/**
 * "There is a conversation here, but nothing new for you" — a plain dot rather
 * than a count, since a stale number reads as something needing attention.
 */
function ActivityDot() {
  return (
    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-slate-400 ring-2 ring-white" />
  );
}

/**
 * A single row in the browser. `type` is 'folder' | 'file'. Folders are
 * navigable; both expose favorite/comments/chat/share/move/rename/trash; files
 * add download, version history, and a details (metadata) editor.
 *
 * Actions are always visible rather than revealed on hover: a hidden comment or
 * chat badge is a message nobody reads, and hover doesn't exist on touch.
 */
export default function ItemRow({
  type,
  item,
  onRename,
  onShare,
  onTrash,
  onDownload,
  onOpen,
  onEdit,
  onVersions,
  onComments,
  onChat,
  onMove,
  onMetadata,
  isFavorite,
  onToggleFavorite,
  commentCount = 0,
  unresolvedCount = 0,
  chatTotal = 0,
  chatUnread = 0,
  onContextMenu,
}) {
  const navigate = useNavigate();
  const isFolder = type === 'folder';

  // Counts belong in the accessible name too — the badge is the whole point of
  // the button, and a screen reader would otherwise hear a bare "Comments".
  let commentLabel = 'Comments';
  if (unresolvedCount > 0) commentLabel = `Comments, ${unresolvedCount} unresolved`;
  else if (commentCount > 0) commentLabel = `Comments, ${commentCount}`;

  let chatLabel = 'Chat';
  if (chatUnread > 0) chatLabel = `Chat, ${chatUnread} unread`;
  else if (chatTotal > 0) chatLabel = `Chat, ${chatTotal} message${chatTotal === 1 ? '' : 's'}`;

  const open = () => {
    if (onOpen) return onOpen({ type, item });
    if (isFolder) navigate(`/folder/${item.id}`);
    else onDownload(item);
  };

  return (
    <div
      className="group flex cursor-pointer items-center gap-3 border-b border-slate-200 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-slate-200/50"
      onDoubleClick={open}
      onContextMenu={(e) => onContextMenu?.(e, type, item)}
    >
      {onToggleFavorite && (
        <button
          title={isFavorite ? 'Unfavorite' : 'Favorite'}
          aria-label="Toggle favorite"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite({ type, item });
          }}
          className={isFavorite ? 'text-amber-400' : 'text-slate-300 hover:text-amber-400'}
        >
          <Star className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      )}

      <div className="flex min-w-0 flex-1 items-center gap-3" onClick={open}>
        {isFolder ? (
          <Folder className="h-5 w-5 shrink-0 text-brand-600" />
        ) : isMalwareBlocked(item) ? (
          <ShieldAlert className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
        ) : isImageFile(item) ? (
          <ImageThumb
            file={item}
            className="h-8 w-8 shrink-0 rounded-md border border-slate-200 object-cover"
            fallback={<FileText className="h-5 w-5 shrink-0 text-slate-400" />}
          />
        ) : (
          <FileText className="h-5 w-5 shrink-0 text-slate-400" />
        )}
        <span className="truncate text-sm font-medium text-slate-700">{item.name}</span>
        {!isFolder && <FileAvailability file={item} />}
        {!isFolder && <MalwareBadge file={item} />}
      </div>

      <div className="hidden w-24 shrink-0 text-right text-xs text-slate-500 sm:block">
        {isFolder ? '—' : formatBytes(item.sizeBytes)}
      </div>
      <div className="hidden w-40 shrink-0 text-right text-xs text-slate-500 md:block">
        {formatDate(item.updatedAt || item.createdAt)}
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {!isFolder && onEdit && isEditableInEditor(item) && (
          <IconButton title="Edit" className={SECONDARY} onClick={() => onEdit(item)}>
            <SquarePen className="h-4 w-4" />
          </IconButton>
        )}
        {!isFolder && onDownload && (
          <IconButton title="Download" className={SECONDARY} onClick={() => onDownload(item)}>
            <Download className="h-4 w-4" />
          </IconButton>
        )}
        {!isFolder && onVersions && (
          <IconButton
            title="Version history"
            className={SECONDARY}
            onClick={() => onVersions({ type, item })}
          >
            <History className="h-4 w-4" />
          </IconButton>
        )}
        {!isFolder && onMetadata && (
          <IconButton
            title="Details"
            className={SECONDARY}
            onClick={() => onMetadata({ type, item })}
          >
            <Info className="h-4 w-4" />
          </IconButton>
        )}
        {onComments && (
          <button
            title={commentLabel}
            aria-label={commentLabel}
            onClick={(e) => {
              e.stopPropagation();
              onComments({ type, item });
            }}
            className="relative rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-600 group-hover:text-slate-500"
          >
            <MessageSquare className="h-4 w-4" />
            {commentCount > 0 && (
              <ActivityBadge count={commentCount} needsAttention={unresolvedCount > 0} />
            )}
          </button>
        )}
        {onChat && (
          <button
            title={chatLabel}
            aria-label={chatLabel}
            onClick={(e) => {
              e.stopPropagation();
              onChat({ type, item });
            }}
            className="relative rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-600 group-hover:text-slate-500"
          >
            <MessagesSquare className="h-4 w-4" />
            {chatUnread > 0 && <ActivityBadge count={chatUnread} needsAttention />}
            {chatUnread === 0 && chatTotal > 0 && <ActivityDot />}
          </button>
        )}
        {onMove && (
          <IconButton title="Move" className={SECONDARY} onClick={() => onMove({ type, item })}>
            <FolderInput className="h-4 w-4" />
          </IconButton>
        )}
        {onShare && (
          <IconButton title="Share" className={SECONDARY} onClick={() => onShare({ type, item })}>
            <Share2 className="h-4 w-4" />
          </IconButton>
        )}
        {onRename && (
          <IconButton title="Rename" className={SECONDARY} onClick={() => onRename({ type, item })}>
            <Pencil className="h-4 w-4" />
          </IconButton>
        )}
        {onTrash && (
          <IconButton
            title="Move to trash"
            danger
            className={SECONDARY}
            onClick={() => onTrash({ type, item })}
          >
            <Trash2 className="h-4 w-4" />
          </IconButton>
        )}
        {onContextMenu && (
          <IconButton
            title={`Actions for ${item.name}`}
            className="md:hidden"
            onClick={(e) => onContextMenu(e, type, item)}
          >
            <MoreHorizontal className="h-4 w-4" />
          </IconButton>
        )}
      </div>
    </div>
  );
}
